import Swiper from 'swiper'
import { Navigation, Pagination, Scrollbar, EffectFade, EffectCreative, Autoplay } from 'swiper/modules'
Swiper.use([Navigation, Pagination, Scrollbar, EffectFade, EffectCreative, Autoplay])
 
// Set default Swiper options
const defaultSwiperOptions = {
  direction: 'horizontal',
  slidesPerView: 1,
  spaceBetween: 20,
  speed: 500,
  loop: false,
  centeredSlidesBound: true,
  centerInsufficientSlides: true,
  watchOverflow: true,
  watchSlidesProgress: true,
  watchSlidesVisibility: true,
  slideVisibleClass: 'swiper-slide-visible',
  a11y: {
    prevSlideMessage: 'Previous slide',
    nextSlideMessage: 'Next slide',
    firstSlideMessage: 'First slide',
    lastSlideMessage: 'Last slide'
  },
  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev'
  },
  pagination: {
    el: '.swiper-pagination',
    type: 'bullets',
    clickable: true
  },
  on: {
    init: function(swiper) {
      swiper.wf = {}
      wfInitializeNavbar(swiper)
      wfUpdateActiveNavbarButton(swiper)
      wfInitializeBackground(swiper)
      wfUpdateActiveBackgroundItem(swiper)
      wfInitializeContentSync(swiper)
      wfUpdateActiveContentSyncItem(swiper)
      wfInitializeNavButtons(swiper)
    },
    slideChange: function(swiper) {
      wfUpdateActiveNavbarButton(swiper)
      wfUpdateActiveBackgroundItem(swiper)
      wfUpdateActiveContentSyncItem(swiper)
    }
  }
}

// Register and initialize Swiper navbar
function wfInitializeNavbar(swiperInstance) {
  const wfSwiperNavbars = swiperInstance.el.closest('.page-component')?.querySelectorAll('.swiper-navbar')
  if (wfSwiperNavbars?.length > 0) {
    swiperInstance.wf.SwiperNavbars = wfSwiperNavbars
    for (let i = 0; i < swiperInstance.wf.SwiperNavbars.length; i++) {
      let allNavbarButtons = swiperInstance.wf.SwiperNavbars[i].querySelectorAll('.swiper-navbar-button')
      allNavbarButtons.forEach((b, i) => {
        b.addEventListener('click', function() {
          swiperInstance.slideTo(i)
        })
      })
    }
  }
}

// If Swiper navbar exists, then mark the current slide's navbar button as active
function wfUpdateActiveNavbarButton(swiperInstance) {
  if (swiperInstance.wf && swiperInstance.wf.SwiperNavbars) {
    for (let i = 0; i < swiperInstance.wf.SwiperNavbars.length; i++) {
      let allNavbarButtons = swiperInstance.wf.SwiperNavbars[i].querySelectorAll('.swiper-navbar-button')
      let currentNavbarButton = allNavbarButtons.item(swiperInstance.realIndex)
      allNavbarButtons.forEach(t => t.classList.remove('swiper-navbar-button-active'))
      if (currentNavbarButton) {
        currentNavbarButton.classList.add('swiper-navbar-button-active')
      }
    }
  }
}

// Register and initialize Swiper background
function wfInitializeBackground(swiperInstance) {
  const wfSwiperBackground = swiperInstance.el.closest('.page-component')?.querySelector('.swiper-background')
  if (wfSwiperBackground) {
    swiperInstance.wf.SwiperBackground = wfSwiperBackground
  }
}

// If Swiper background exists, then mark the current slide's background item as active
function wfUpdateActiveBackgroundItem(swiperInstance) {
  if (swiperInstance.wf && swiperInstance.wf.SwiperBackground) {
    const allBackgroundItems = swiperInstance.wf.SwiperBackground.querySelectorAll('.swiper-background-item')
    const currentBackgroundItem = allBackgroundItems.item(swiperInstance.realIndex)
    allBackgroundItems.forEach(t => t.classList.remove('swiper-background-item-active'))
    if (currentBackgroundItem) {
      currentBackgroundItem.classList.add('swiper-background-item-active')
    }
  }
}

// Register and initialize Swiper content sync
function wfInitializeContentSync(swiperInstance) {
  const wfSwiperContentSync = swiperInstance.el.closest('.page-component')?.querySelectorAll('.swiper-content-sync')
  if (wfSwiperContentSync) {
    swiperInstance.wf.wfSwiperContentSync = wfSwiperContentSync
  }
}

// If Swiper content sync exists, then mark the current slide's content sync items as active
function wfUpdateActiveContentSyncItem(swiperInstance) {
  if (swiperInstance.wf && swiperInstance.wf.wfSwiperContentSync) {
    swiperInstance.wf.wfSwiperContentSync.forEach(sync => {
      const allSyncItems = sync.querySelectorAll('.swiper-content-sync-item')
      const currentSyncItem = allSyncItems.item(swiperInstance.realIndex)
      allSyncItems.forEach(t => t.classList.remove('active'))
      if (currentSyncItem) {
        currentSyncItem.classList.add('active')
      }
    })
  }
}

// Register and initialize nav buttons
function wfInitializeNavButtons(swiperInstance) {
  const wfSwiperNavPrevButtons = swiperInstance.el.closest('.page-component')?.querySelectorAll('.swiper-nav-button-prev')
  const wfSwiperNavNextButtons = swiperInstance.el.closest('.page-component')?.querySelectorAll('.swiper-nav-button-next')

  wfSwiperNavPrevButtons?.forEach(button => {
    button.addEventListener('click', function(event) {
      swiperInstance.slidePrev()
    })
  })

  wfSwiperNavNextButtons?.forEach(button => {
    button.addEventListener('click', function(event) {
      swiperInstance.slideNext()
    })
  })
}

// Initializes a Swiper instance on the given element with the provided options
function initializeSwiper(wrapperSelector, additionalOptions) {
  const wrapperElements = document.querySelectorAll(wrapperSelector)
  wrapperElements.forEach((wrapperElement) => {
    const swiperElement = wrapperElement.querySelector('.swiper')
    const swiperDataOptions = JSON.parse(swiperElement?.dataset.swiper ?? null)
    const options = {...defaultSwiperOptions, ...additionalOptions, ...swiperDataOptions }
    
    // Replace autoplay = true with default autoplay settings
    if (options.autoplay) {
      options.autoplay = {
        delay: 10000
      }
    }
    
    // Replace the specific pagination and navigation objects with child elements selected from this wrapper
    if (options.pagination) {
      options.pagination = {
        el: wrapperElement.querySelector(options.pagination.el),
        type: options.pagination.type,
        clickable: options.pagination.clickable
      }
    }
    if (options.navigation) {
      options.navigation = {
        prevEl: wrapperElement.querySelector(options.navigation.prevEl),
        nextEl: wrapperElement.querySelector(options.navigation.nextEl)
      }
    }

    const swiper = new Swiper(swiperElement, options)
    return swiper
  })
}

// Initialize Swiper instances
export function initializeSwiperInstances() {
  initializeSwiper(
    '.carousel',
    {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: true,
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      }
    }
  )
}

document.addEventListener('DOMContentLoaded', (event) => {
  initializeSwiperInstances()
})